// @ts-ignore-next-line
import { isEqual } from 'lodash-es'
import moment, { Moment } from 'moment-timezone'
import React from 'react'

import { formatTime } from 'services/time_format'

// Types for meeting groups - support both date formats (formatted string or rawDate)
export interface MeetingGroup {
  rawDate?: string
  date: string
  events?: Array<{
    startTime?: string
    endTime?: string
    cancelled?: boolean
    placeholder?: boolean
    past?: boolean
    meetingIntelligence?: {
      status: string
    }
    [key: string]: unknown
  }>
  [key: string]: unknown
}

export interface MeetingsMetadata {
  numberOfEventsTodayAndAfter: number
  hasEventsAfterToday: boolean
  hasFutureEvents: boolean
  hasPastEvents: boolean
  hasMeetingInProgress: boolean
}

/**
 * Finds the index of today's meeting group in the meetings array.
 * Supports both `date` (formatted) and `rawDate` (YYYY-MM-DD) formats.
 */
export const findTodayIndex = (
  meetings: MeetingGroup[],
  timezone: string,
  now?: Moment
): number => {
  if (!meetings || !Array.isArray(meetings) || !timezone) {
    return -1
  }

  const today = now ? now.clone().tz(timezone) : moment().tz(timezone)

  return meetings.findIndex((group) => {
    if (!group) {
      return false
    }

    // Try rawDate first (YYYY-MM-DD format), then fall back to date
    const dateToCheck = group.rawDate || group.date
    if (!dateToCheck) {
      return false
    }

    // If it's already in YYYY-MM-DD format (rawDate), parse directly
    if (group.rawDate) {
      const groupDate = moment.tz(group.rawDate, timezone)
      return groupDate.isSame(today, 'day')
    }

    // Otherwise, parse the formatted date string
    const groupDate = moment.tz(group.date, timezone)
    return groupDate.isSame(today, 'day')
  })
}

/**
 * Gets the total number of events from today onwards.
 */
export const getNumberOfEventsTodayAndAfter = (
  meetings: MeetingGroup[],
  timezone: string,
  now?: Moment
): number => {
  if (!meetings || !Array.isArray(meetings)) {
    return 0
  }

  const todayIndex = findTodayIndex(meetings, timezone, now)

  if (todayIndex === -1) {
    return 0
  }

  return meetings
    .slice(todayIndex)
    .reduce((count, group) => {
      if (!group || !group.events || !Array.isArray(group.events)) {
        return count
      }
      return count + group.events.length
    }, 0)
}

/**
 * Checks if there's a meeting currently in progress for today.
 */
export const isMeetingInProgress = (
  today: MeetingGroup | undefined,
  timezone: string,
  now?: Moment
): boolean => {
  if (!today || !timezone || !today.events || !Array.isArray(today.events)) {
    return false
  }

  const currentTime = now ? now.clone().tz(timezone) : moment().tz(timezone)

  const inProgressEvent = today.events
    .filter((event): event is NonNullable<typeof event> => event !== null && event !== undefined)
    .find((event) => (
      event
        && !event.placeholder
        && event.startTime
        && event.endTime
        && currentTime.isBetween(
          moment.tz(event.startTime, timezone),
          moment.tz(event.endTime, timezone)
        )
        && !event.cancelled
    ))

  return !!inProgressEvent
}

/**
 * Gets metadata about meetings including counts and status flags.
 */
export const getMeetingsMetadata = (
  meetings: MeetingGroup[],
  timezone: string,
  now?: Moment
): MeetingsMetadata => {
  if (!meetings || !Array.isArray(meetings) || !timezone) {
    return {
      numberOfEventsTodayAndAfter: 0,
      hasEventsAfterToday: false,
      hasFutureEvents: false,
      hasPastEvents: false,
      hasMeetingInProgress: false
    }
  }

  const today = now ? now.clone().tz(timezone) : moment().tz(timezone)
  const todayIndex = findTodayIndex(meetings, timezone, now)

  // Helper to get the date string for comparison (prefer rawDate, fallback to date)
  const getDateForComparison = (group: MeetingGroup): string | null => group.rawDate || group.date || null

  return {
    numberOfEventsTodayAndAfter: getNumberOfEventsTodayAndAfter(meetings, timezone, now),
    hasEventsAfterToday: meetings
      .filter((group) => {
        const dateStr = getDateForComparison(group)
        return dateStr && moment.tz(dateStr, 'YYYY-MM-DD', timezone).isAfter(today, 'day')
      })
      .some((group) => (
        group.events
          && Array.isArray(group.events)
          && group.events.filter((event) => event && !event.placeholder).length > 0
      )),
    hasFutureEvents: meetings
      .filter((group) => {
        const dateStr = getDateForComparison(group)
        return dateStr && moment.tz(dateStr, 'YYYY-MM-DD', timezone).isSameOrAfter(today, 'day')
      })
      .some((group) => (
        group.events
          && Array.isArray(group.events)
          && group.events.filter((event) => event && !event.placeholder).length > 0
      )),
    hasPastEvents: meetings
      .filter((group) => {
        const dateStr = getDateForComparison(group)
        return dateStr && moment.tz(dateStr, 'YYYY-MM-DD', timezone).isBefore(today, 'day')
      })
      .some((group) => (
        group.events
          && Array.isArray(group.events)
          && group.events.filter((event) => event && !event.placeholder).length > 0
      )),
    hasMeetingInProgress: isMeetingInProgress(meetings[todayIndex], timezone, now)
  }
}

/**
 * Scrolls to today's meeting group in the container.
 * Uses multiple strategies: progress indicator, data-date attribute, or index-based lookup.
 */
export const scrollToToday = (
  containerRef: React.RefObject<HTMLDivElement>,
  meetings: MeetingGroup[],
  timezone: string,
  smooth = false,
  now?: Moment
): void => {
  if (typeof window === 'undefined') {
    return
  }

  const container = containerRef.current
  if (!container) {
    return
  }

  // Priority 1: Try to scroll to progress indicator
  const indicator = container.querySelector('[data-testid="current-time-indicator"]')
  if (indicator) {
    try {
      indicator.scrollIntoView({ block: 'center', behavior: smooth ? 'smooth' : 'auto' })
    } catch (error) {
      // Ignore scroll errors gracefully
    }
    return
  }

  // Priority 2: Scroll to today's date group using data-date attribute
  const today = now ? now.clone().tz(timezone) : moment().tz(timezone)
  const todayStr = today.format('YYYY-MM-DD')
  const todayGroup = container.querySelector(`[data-date="${todayStr}"]`)
  if (todayGroup) {
    try {
      todayGroup.scrollIntoView({ block: 'start', behavior: smooth ? 'smooth' : 'auto', inline: 'nearest' })
    } catch (error) {
      // Ignore scroll errors gracefully
    }
    return
  }

  // Priority 3: Fallback to original logic (using index)
  const todayIndex = findTodayIndex(meetings, timezone, now)
  let targetElement: HTMLElement | null = null

  if (todayIndex !== -1) {
    // Find the MeetingGroup element for today
    // The container has Bookend as first child, then MeetingGroups
    const childIndex = todayIndex + 1
    if (childIndex < container.children.length) {
      targetElement = container.children[childIndex] as HTMLElement
    }
  } else {
    // Scroll to last group if today not found
    const lastIndex = container.children.length - 2
    if (lastIndex >= 0) {
      targetElement = container.children[lastIndex] as HTMLElement
    }
  }

  if (!targetElement) {
    return
  }

  // scroll-margin-top is now handled via CSS on the EventsGroup component
  // This provides a more maintainable solution that automatically accounts for sticky headers
  try {
    targetElement.scrollIntoView({
      behavior: smooth ? 'smooth' : 'auto',
      block: 'start',
      inline: 'nearest'
    })
  } catch (error) {
    // Ignore scroll errors gracefully
  }
}

/**
 * Scrolls the list to a specific date group (by `YYYY-MM-DD`), used when the meetings feed
 * re-centers on a date picked in the selector. Unlike scrollToToday it does not target the
 * current-time indicator — the anchor day is not necessarily today.
 */
export const scrollToDate = (
  containerRef: React.RefObject<HTMLDivElement>,
  dateStr: string,
  smooth = false
): void => {
  if (typeof window === 'undefined') return
  const container = containerRef.current
  if (!container || !dateStr) return
  const group = container.querySelector(`[data-date="${dateStr}"]`)
  if (!group) return
  try {
    group.scrollIntoView({ block: 'start', behavior: smooth ? 'smooth' : 'auto', inline: 'nearest' })
  } catch (error) {
    // Ignore scroll errors gracefully
  }
}

/**
 * Gets the first visible Meeting Intelligence event from meetings.
 */
export function getFirstVisibleMIEvent(
  meetings: MeetingGroup[]
): { meetingIntelligence?: { status: string }; [key: string]: unknown } | undefined {
  const allEvents = meetings.flatMap((group) => group.events || [])
  return allEvents.find(
    (event) => event
      && !event.past
      && !event.cancelled
      && event.meetingIntelligence
      && ['SCHEDULED'].includes(event.meetingIntelligence.status)
  )
}

/**
 * Summarizes a time interval with proper formatting based on time notation.
 */
export const summarizeInterval = (
  { from, to }: { from: Moment; to: Moment },
  timeNotation: '12h' | '24h',
  includeTimezone = false
): string => {
  const fromMeridian = from.format('a')
  const toMeridian = to.format('a')

  if (timeNotation === '24h') {
    let result = `${formatTime(from, timeNotation)} - ${formatTime(to, timeNotation)}`

    if (includeTimezone) {
      result += ` (${from.format('z')})`
    }

    return result
  }

  let fromFormat = 'h'
  if (!isEqual(from.format('mm'), '00')) {
    fromFormat += ':mm'
  }
  if (!isEqual(fromMeridian, toMeridian)) {
    fromFormat += ' a'
  }

  let toFormat = 'h'
  if (!isEqual(to.format('mm'), '00')) {
    toFormat += ':mm'
  }
  toFormat += ' a'

  let interval = `${from.format(fromFormat)} – ${to.format(toFormat)}`

  if (includeTimezone) {
    interval += ` (${from.format('z')})`
  }

  return interval
}

/**
 * Checks if a meeting is a Calendly meeting (default source).
 */
export const isCalendlyMeeting = (meeting: { source?: string }): boolean => !meeting.source || meeting.source === 'CALENDLY'

interface BlockableInvitee {
  schedulingMethod?: string | null
  inviteeScheduledBy?: { id?: number | null } | null
}

/**
 * Whether the Block/Unblock invitee actions should be hidden for this invitee.
 *
 * `inviteeScheduledBy` is populated whenever the booking carried a Calendly session
 * (the booking app forwards the user JWT and meetings-api stamps `invitee.creator`),
 * which is NOT a reliable book-on-behalf signal — incidental logged-in invitees
 * trip it too. Gate on the actual BoB flag (`schedulingMethod === 'instant_book'`,
 * only set when `book_on_behalf=true` reaches the scheduling service) or on the
 * booker being the viewing user (so a host who self-books can't block themselves).
 */
export const isInviteeBookedOnBehalf = (
  invitee: BlockableInvitee | null | undefined,
  currentUserId: number | null | undefined
): boolean => {
  if (!invitee) return false
  if (invitee.schedulingMethod === 'instant_book') return true
  const scheduledByUserId = invitee.inviteeScheduledBy?.id
  return scheduledByUserId != null && currentUserId != null && scheduledByUserId === currentUserId
}
